import {
  createStateSyncMiddleware,
  initMessageListener,
  Config as SyncConfig,
} from 'redux-state-sync';
import { syncBlacklist, syncWhitelist } from './features/sync';

// Helper function to check if an action starts with any of the blacklisted prefixes
const isBlacklisted = (actionType: string): boolean => {
  return syncBlacklist.some(prefix => actionType.startsWith(prefix));
};

// Helper function to check if an action is whitelisted
const isWhitelisted = (actionType: string): boolean => {
  return syncWhitelist.includes(actionType);
};

// Helper function to check if an action is a sync action (prevents loops)
const isSyncAction = (actionType: string): boolean => {
  return actionType.startsWith('@@redux-state-sync/');
};

// Helper function to check if an action is serializable
const isSerializable = (value: any): boolean => {
  if (
    value === undefined ||
    value === null ||
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    typeof value === 'string'
  ) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every(isSerializable);
  }

  if (typeof value === 'object') {
    return Object.values(value).every(isSerializable);
  }

  return false;
};

// Configuration for redux-state-sync
export const syncConfig: SyncConfig = {
  // Use dynamic whitelist from registered features
  get whitelist() {
    return syncWhitelist;
  },
  // Custom channel name to avoid conflicts with other apps
  channel: 'jotpsych-tab-sync',
  // Use BroadcastChannel API for better performance
  broadcastChannelOption: {
    type: 'native' as const,
  },
  // Strict predicate to ensure only safe actions are synced
  predicate: action => {
    // Prevent sync loops - never sync actions from other tabs
    if (isSyncAction(action.type)) {
      return false;
    }

    // Skip blacklisted actions (using proper prefix matching)
    if (isBlacklisted(action.type)) {
      return false;
    }

    // Only sync whitelisted actions for security and performance
    if (!isWhitelisted(action.type)) {
      return false;
    }

    // Additional safety check for serializability
    try {
      if (action.payload && !isSerializable(action.payload)) {
        console.warn(`Skipping non-serializable action sync: ${action.type}`);
        return false;
      }
      JSON.stringify(action);
      return true;
    } catch (e) {
      console.warn(`Action not synchronizable: ${action.type}`, e);
      return false;
    }
  },
};

// Export the middleware for use in the store
export const createSyncMiddleware = () => {
  // Create middleware with enhanced error handling to prevent crashes
  const middleware = createStateSyncMiddleware(syncConfig) as any;

  // Wrap the middleware to catch any errors
  return (store: any) => (next: any) => (action: any) => {
    try {
      // Process the action through the redux-state-sync middleware
      return middleware(store)(next)(action);
    } catch (error) {
      // If we get a DataCloneError or any other serialization error,
      // log it and pass the action through to the next middleware
      console.warn(`Redux-state-sync error for action ${action.type}:`, error);
      return next(action);
    }
  };
};

// Initialize the message listener that will listen for actions from other tabs
export const initStateListener = (store: any) => {
  initMessageListener(store);
};
